import { forwardRef, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { StepProps, StepRef } from "../../types";
import useIsMobile from "components/hooks/useIsMobile";
import StaticStep from "../../components/StaticStep";
import PasswordInput from "components/common/PasswordInput";
import { validatePassword } from "common/utils";
import AuthService from "services/auth";
import { useSendExistingAccountConflictEmail } from "services/signup";
import TextField from "components/common/TextField";
import Checkbox from "components/common/Checkbox";
import { TOS_VERSION } from "components/context/CheckTerms";
import { getBrowserTimezone } from "common/timezoneUtils";
import TokenStore from "services/TokenStore";
import { useNavigate } from "react-router-dom";
import PhoneConflictDialog, { PhoneConflictState } from "./PhoneConflictDialog";

interface FormData {
  email: string;
  password: string;
}

interface FormErrors {
  [key: string]: string;
}

export default forwardRef<StepRef, StepProps>(
  ({ onNext, disabled, signupState }, _ref) => {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const isAccountCreated = !!TokenStore.getLocalCurrentUser();
    const [formData, setFormData] = useState<FormData>({
      email: signupState?.userInfo?.email || "",
      password: "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [phoneConflictState, setPhoneConflictState] =
      useState<PhoneConflictState>(null);
    const [hasAcceptedToS, setHasAcceptedToS] = useState(false);
    const isComplete = isAccountCreated
      ? !!formData.password.trim()
      : !!formData.email.trim() && !!formData.password.trim() && hasAcceptedToS;

    const { request: createPasswordRequest, loading: createPasswordLoading } =
      AuthService.useCreatePassword();
    const {
      request: sendExistingAccountConflictEmail,
      loading: sendExistingAccountConflictEmailLoading,
    } = useSendExistingAccountConflictEmail();

    const validateForm = (): FormErrors => {
      const newErrors: FormErrors = {};

      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }

      Object.assign(newErrors, validatePassword(formData.password));
      return newErrors;
    };

    const createChangeHandler = (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    const handleContinue = async () => {
      const passwordErrors = validatePassword(formData.password);
      const validationErrors = isAccountCreated
        ? passwordErrors
        : validateForm();
      if (!isAccountCreated && !hasAcceptedToS) {
        validationErrors.termsOfService =
          "You must accept the terms of service";
      }
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      try {
        if (isAccountCreated) {
          if (!AuthService.getCurrentUser()?.isPasswordSet) {
            await createPasswordRequest({ password: formData.password });
          }
          await onNext?.({});
        } else {
          const timezone = getBrowserTimezone();
          const response = await onNext?.({
            userInfo: {
              email: formData.email,
              firstName: signupState?.userInfo?.firstName || "",
              lastName: signupState?.userInfo?.lastName || "",
              phone: signupState?.userInfo?.phone || "",
              address: signupState?.userInfo?.address || "",
              timezone,
            },
            termsOfServiceVersion: TOS_VERSION,
          });
          if (response && "token" in response && response.token) {
            const loginResponse = await AuthService.signupTokenLogin(
              response.token,
            );
            if (!loginResponse?.currentUser?.isPasswordSet) {
              await createPasswordRequest({ password: formData.password });
            }
          }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "";
        if (message === "account_conflict_confirmation_required") {
          setPhoneConflictState("confirm");
          setErrors({});
        } else if (message === "account_conflict_email_sent") {
          setPhoneConflictState("emailSent");
          setErrors({});
        } else {
          setErrors({ general: "Failed to create account. Please try again." });
        }
      }
    };

    const handleCreateNewDigitalAccount = async () => {
      try {
        const response = await sendExistingAccountConflictEmail({ token: "" });
        if (response) {
          setPhoneConflictState("emailSent");
          setErrors({});
        }
      } catch {
        setErrors({ general: "Failed to send email. Please try again." });
      }
    };

    const handleSignInToExistingAccount = () => {
      navigate(
        `/member-login/link?email=${encodeURIComponent(formData.email.trim())}`,
      );
    };

    const conflictEmail = formData.email.trim();

    return (
      <>
        <StaticStep
          title="Secure your account"
          img="/assets/images/nux/signup-confirmation.png"
          mobileImg="/assets/images/nux/signup-confirmation-mobile.png"
          fullImageHeight={true}
          mobileImgSx={{
            borderRadius: "0",
            margin: "-16px auto 8px auto",
            marginBottom: "8px",
            maxHeight: "30vh",
            width: "auto",
            borderBottom: "1px solid #D4D4D4",
          }}
          bodyContent={
            <>
              <Typography variant="body" sx={{ marginBottom: "8px" }}>
                Your email and password are used to secure your data. You can
                also use this address to forward documents to Faye when you want
                your bot to add events, todos, or take other actions from an
                email.
              </Typography>

              {errors.general && !phoneConflictState && (
                <Alert severity="error" sx={{ marginBottom: "16px" }}>
                  {errors.general}
                </Alert>
              )}

              {!isAccountCreated && (
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  name="email"
                  autoComplete="email"
                  value={formData.email}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    createChangeHandler("email", e.target.value)
                  }
                />
              )}

              <PasswordInput
                password={formData.password}
                onChange={(password: string) =>
                  createChangeHandler("password", password)
                }
                sx={{ width: "100%" }}
              />

              {!isAccountCreated && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <Checkbox
                      checked={hasAcceptedToS}
                      onChange={() => {
                        setHasAcceptedToS(!hasAcceptedToS);
                        if (errors.termsOfService) {
                          setErrors((prev) => ({
                            ...prev,
                            termsOfService: "",
                          }));
                        }
                      }}
                    />
                    <Typography variant="body">
                      I agree to the{" "}
                      <a
                        href="https://www.findfaye.com/client-terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#198282",
                          textDecoration: "none",
                        }}
                      >
                        Terms of Service
                      </a>
                      .
                    </Typography>
                  </label>
                  {errors.termsOfService && (
                    <Typography variant="caption" color="error">
                      {errors.termsOfService}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          }
          actions={
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size={isMobile ? "medium" : "large"}
                disabled={disabled || createPasswordLoading || !isComplete}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </Box>
          }
        />
        <PhoneConflictDialog
          open={!!phoneConflictState}
          state={phoneConflictState}
          email={conflictEmail}
          error={errors.general}
          loading={disabled || sendExistingAccountConflictEmailLoading}
          onCreateNewDigitalAccount={handleCreateNewDigitalAccount}
          onSignInToExistingAccount={handleSignInToExistingAccount}
        />
      </>
    );
  },
);
